import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Calendar, Star, Phone, Mail, FileText, Hammer, Briefcase, Check, X, Loader2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ContractorDetailPage() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [c, setC] = useState(null);
  const [workmen, setWorkmen] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setRosterLoading(true);
    try {
      const cRes = await api.get(`/contractors/${id}`);
      setC(cRes.data);
    } catch (e) {
      setC(null);
    } finally {
      setLoading(false);
    }
    try {
      const wRes = await api.get(`/contractors/${id}/workmen`);
      setWorkmen(wRes.data || []);
    } catch (e) {
      setWorkmen([]);
    } finally {
      setRosterLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
  }, [id]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (loading) return <div className="mx-auto max-w-6xl px-4 py-20 font-mono text-slate-600">Loading dossier…</div>;
  if (!c) return <div className="mx-auto max-w-6xl px-4 py-20 font-mono text-slate-600">Contractor not found.</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
      <Link to="/directory" data-testid="back-to-directory" className="inline-flex items-center gap-2 industrial-label text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to directory
      </Link>

      <div className="mt-6 grid gap-8 md:grid-cols-12">
        {/* LEFT */}
        <aside className="md:col-span-4">
          <div className="border-2 border-slate-900 bg-white">
            <div className="relative h-72 border-b-2 border-slate-900 bg-slate-100">
              {c.photo?.path ? (
                <img src={fileUrl(c.photo.path)} alt={c.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-grid-light">
                  <div className="font-heading text-7xl font-black text-slate-300">{c.name[0]}</div>
                </div>
              )}
              <div className="absolute left-0 top-0 flex items-center gap-1.5 bg-slate-900 px-2.5 py-1.5 text-white">
                <Star className="h-3.5 w-3.5 fill-safety-500 text-safety-500" />
                <span className="font-mono text-sm font-bold">{c.avg_rating?.toFixed(1) || "—"}</span>
                <span className="industrial-label text-slate-400">·{c.review_count}</span>
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Detail icon={<Briefcase className="h-4 w-4" />} label="Type" value={c.contractor_type || "Labour Contractor"} />
              {c.current_status && (
                <Detail
                  icon={<Briefcase className="h-4 w-4" />}
                  label="Status"
                  value={c.current_status === "NCCL" ? `NCCL · ${c.ncc_site_name || "—"}` : "NEW"}
                />
              )}
              {c.id_proof_type && c.id_proof_number && (
                <Detail icon={<FileText className="h-4 w-4" />} label={c.id_proof_type} value={c.id_proof_number} mono />
              )}
              <Detail icon={<Phone className="h-4 w-4" />} label="Phone" value={c.contact_phone} mono />
              {c.contact_email && <Detail icon={<Mail className="h-4 w-4" />} label="Email" value={c.contact_email} mono />}
              <Detail icon={<MapPin className="h-4 w-4" />} label="Location" value={c.location} />
              <Detail icon={<Users className="h-4 w-4" />} label="Crew" value={`${c.crew_size} workers`} mono />
              <Detail icon={<Calendar className="h-4 w-4" />} label="Experience" value={`${c.experience_years} years`} mono />
            </div>
          </div>
        </aside>

        {/* RIGHT */}
        <main className="md:col-span-8 space-y-8">
          <div>
            <div className="industrial-label text-safety-600">// Contractor Dossier</div>
            <h1 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">{c.name}</h1>
            <div className="mt-3 flex flex-wrap gap-2">
              {(c.trades || []).map((t) => {
                const n = c.trade_counts?.[t];
                return (
                  <span key={t} className="border-2 border-slate-900 bg-safety-500 px-3 py-1 industrial-label text-slate-900">
                    {t}{n ? ` · ${n}` : ""}
                  </span>
                );
              })}
            </div>
            {c.bio && <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700">{c.bio}</p>}
          </div>

          {/* Past projects */}
          {c.projects?.length > 0 && (
            <Section title="Past Projects" icon={<Hammer className="h-4 w-4" />}>
              <div className="grid gap-4 sm:grid-cols-2">
                {c.projects.map((p, i) => (
                  <div key={i} className="border-2 border-slate-900 bg-white">
                    {p.image?.path && <img src={fileUrl(p.image.path)} alt={p.title} className="h-40 w-full border-b-2 border-slate-900 object-cover" />}
                    <div className="p-4">
                      <div className="font-heading font-bold text-slate-900">{p.title}</div>
                      {p.year && <div className="industrial-label text-slate-500">{p.year}</div>}
                      {p.description && <p className="mt-2 text-sm text-slate-700">{p.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Certifications */}
          {c.certifications?.length > 0 && (
            <Section title="Certifications">
              <DocsGrid docs={c.certifications} />
            </Section>
          )}

          {/* ID Docs */}
          {c.id_docs?.length > 0 && (
            <Section title="ID & License Documents">
              <DocsGrid docs={c.id_docs} />
            </Section>
          )}

          {/* Manpower Roster */}
          <Section title={`Manpower Roster (${workmen.length})`} icon={<Users className="h-4 w-4" />}>
            <div className="overflow-x-auto border-2 border-slate-900 bg-white" data-testid="dossier-manpower-roster">
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="bg-slate-900 text-left text-white">
                    <th className="px-3 py-2 industrial-label">Sr No.</th>
                    <th className="px-3 py-2 industrial-label">Photo</th>
                    <th className="px-3 py-2 industrial-label">Name</th>
                    <th className="px-3 py-2 industrial-label">Trade</th>
                    <th className="px-3 py-2 industrial-label">Mobile No</th>
                    <th className="px-3 py-2 industrial-label">Aadhar No.</th>
                    <th className="px-3 py-2 industrial-label">UAN No</th>
                    <th className="px-3 py-2 text-center industrial-label">Checklist (Medical / Induction)</th>
                  </tr>
                </thead>
                <tbody>
                  {rosterLoading ? (
                    <tr><td colSpan={8} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
                  ) : workmen.length === 0 ? (
                    <tr><td colSpan={8} className="p-6 text-center font-mono text-slate-500">No workmen registered under this contractor yet.</td></tr>
                  ) : workmen.map((w, i) => {
                    const medicalDone = !!(w.vision || w.hearing || w.phys_exam_blood_pressure || w.phys_exam_pulse || w.physical_deformities || w.medical_history);
                    const inductionDone = w.status === "approved";
                    return (
                      <tr key={w.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`dossier-roster-row-${w.id}`}>
                        <td className="px-3 py-2 font-mono">{i + 1}</td>
                        <td className="px-3 py-2">
                          {w.photo_base64 ? (
                            <img src={w.photo_base64} alt={w.name} className="h-10 w-10 border border-slate-300 object-cover" />
                          ) : (
                            <div className="grid h-10 w-10 place-items-center border border-slate-300 bg-slate-100 font-heading text-sm font-black text-slate-400">
                              {(w.name || "?")[0]}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-bold text-slate-900">{w.name}</td>
                        <td className="px-3 py-2">{w.trade || "—"}</td>
                        <td className="px-3 py-2 font-mono">{w.mobile || "—"}</td>
                        <td className="px-3 py-2 font-mono">{w.aadhaar || w.id_proof_number || "—"}</td>
                        <td className="px-3 py-2 font-mono">{w.uan_no || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-3">
                            <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[11px] ${medicalDone ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`} data-testid={`dossier-medical-${w.id}`}>
                              Medical {medicalDone ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            </span>
                            <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[11px] ${inductionDone ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`} data-testid={`dossier-induction-${w.id}`}>
                              Induction {inductionDone ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 font-mono text-[11px] text-slate-500">
              <b>Medical</b> ✓ = medical form data captured. <b>Induction</b> ✓ = safety induction approved.
            </p>
          </Section>
        </main>
      </div>
    </div>
  );
}

function Detail({ icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 border-b border-slate-200 pb-3 last:border-b-0">
      <div className="mt-0.5 flex h-7 w-7 items-center justify-center border-2 border-slate-900 bg-safety-500">{icon}</div>
      <div className="flex-1">
        <div className="industrial-label text-slate-500">{label}</div>
        <div className={`${mono ? "font-mono" : ""} text-slate-900`}>{value}</div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 border-b-2 border-slate-900 pb-2">
        {icon}
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function DocsGrid({ docs }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {docs.map((d, i) => {
        const isImg = (d.content_type || "").startsWith("image/");
        return (
          <a key={i} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="block border-2 border-slate-900 bg-white p-2 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm">
            {isImg ? (
              <img src={fileUrl(d.path)} alt={d.name} className="h-32 w-full object-cover" />
            ) : (
              <div className="flex h-32 items-center justify-center bg-slate-100">
                <FileText className="h-10 w-10 text-slate-500" />
              </div>
            )}
            <div className="mt-2 truncate font-mono text-xs text-slate-700">{d.name}</div>
          </a>
        );
      })}
    </div>
  );
}
